/** Column definitions for the external-ID mappings grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Sortable
 * column FIELDS equal the backend ordering whitelist keys (id, tenant, value,
 * kind, model, target, id_type, matching, queryable, integration, account,
 * created_ts) so the sort model round-trips as the DRF `ordering` param —
 * everything else is `sortable: false` (the dead-sort-header bug class).
 *
 * `used_by`, `tags` and `fanout` are derived or drawer-only and cannot be
 * ordered server-side, so they say so rather than rendering a header that
 * silently does nothing.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import type { ExtIdListItem } from '@/api/extIds';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { FacetCell, KindChip, ManagedChip, ModelChip, ParkedChip, dash, mono } from './extIdFormat';

const CHIP_TRIGGER_SX = {
  display: 'inline-flex',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'none' },
} as const;

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

/** Where a mapped entity lives in the console. */
function targetHref(row: ExtIdListItem): string | null {
  switch (row.model) {
    case 'tour':
      return `/tours/${row.object_id}`;
    case 'itinerary':
      return `/itineraries/${row.object_id}`;
    case 'activity':
      return `/activities/${row.object_id}`;
    default:
      return null;
  }
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to a mapping's detail drawer (keeps the grid's query string). */
  detailPath: (id: string) => string;
  /** Open the row kebab (edit, take over / hand back, delete). Omitted when the
   *  caller renders no actions. */
  onRowMenu?: (anchor: HTMLElement, row: ExtIdListItem) => void;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<ExtIdListItem>[] {
  const { showTenant, onQuickFilter, onRowMenu } = opts;

  const cols: GridColDef<ExtIdListItem>[] = [];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 150,
      sortable: true,
      filterable: false,
      renderCell: (p) => (
        <WithTenantMark environment={p.row.tenant.environment}>
          <CellActionsPopover
            auto
            actions={[
              {
                key: 'open',
                label: 'Open tour operator',
                icon: <OpenInNewIcon fontSize="small" />,
                href: `/tour-operators/${p.row.tenant.id}`,
              },
              filterAction('Filter by this tenant', 'tenant', p.row.tenant.code, onQuickFilter),
            ]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {p.row.tenant.name}
          </CellActionsPopover>
        </WithTenantMark>
      ),
    });
  }

  cols.push(
    {
      field: 'value',
      headerName: 'External ID',
      flex: 1.4,
      minWidth: 240,
      sortable: true,
      filterable: false,
      renderCell: (p) => {
        const raw = p.row.value;
        // Whitespace is load-bearing: get_tour_map strips, is_custom_id_unique
        // does not, so ' X' and 'X' are one key to the matcher and two rows
        // here. Make it visible rather than let it look like a duplicate.
        const padded = raw !== raw.trim();
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
            <CellActionsPopover
              auto
              title={padded ? `"${raw}" — note the surrounding whitespace` : undefined}
              actions={[
                copyAction('Copy external ID', raw),
                filterAction('Filter by this ID', 'value', raw.trim(), onQuickFilter),
              ]}
              sx={{ ...mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {padded ? `␣${raw.trim()}␣` : raw}
            </CellActionsPopover>
            <ParkedChip marker={p.row.parked_marker} useForMatching={p.row.use_for_matching} />
            {p.row.managed && (
              <ManagedChip integration={p.row.integration} restechAccount={p.row.restech_account} />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'target',
      headerName: 'Maps to',
      flex: 1.2,
      minWidth: 200,
      sortable: true,
      filterable: false,
      renderCell: (p) => {
        const href = targetHref(p.row);
        if (p.row.is_dangling) {
          return (
            <Tooltip title="This alias points at an entity that no longer exists. Nothing cascades a generic foreign key, so the row was left behind.">
              <Box component="span" sx={{ color: 'error.main' }}>
                missing (#{p.row.object_id})
              </Box>
            </Tooltip>
          );
        }
        const actions: CellAction[] = [];
        if (href) {
          actions.push({
            key: 'open',
            label: `Open ${p.row.model}`,
            icon: <OpenInNewIcon fontSize="small" />,
            href,
          });
        }
        actions.push(
          filterAction(`Filter by ${p.row.model}s`, 'model', p.row.model, onQuickFilter),
          filterAction('Filter by this entity', 'target', String(p.row.object_id), onQuickFilter),
        );
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
            <ModelChip model={p.row.model} />
            <CellActionsPopover
              auto
              actions={actions}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.target.name ?? `#${p.row.object_id}`}
            </CellActionsPopover>
          </Stack>
        );
      },
    },
    {
      field: 'kind',
      headerName: 'Kind',
      width: 160,
      sortable: true,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <CellActionsPopover
            auto
            actions={[filterAction(`Filter by ${p.row.kind}`, 'kind', p.row.kind, onQuickFilter)]}
            sx={CHIP_TRIGGER_SX}
          >
            <KindChip kind={p.row.kind} />
          </CellActionsPopover>
          {p.row.id_type ? (
            <CellActionsPopover
              auto
              title="Ops-maintained label. Matching never reads it."
              actions={[
                filterAction('Filter by this ID type', 'id_type', p.row.id_type, onQuickFilter),
                copyAction('Copy ID type', p.row.id_type),
              ]}
              sx={{ fontSize: 12, color: 'text.secondary' }}
            >
              {p.row.id_type}
            </CellActionsPopover>
          ) : null}
        </Stack>
      ),
    },
    {
      field: 'used_by',
      headerName: 'Used by',
      width: 230,
      // Derived per row from the tenant's configured integrations — there is
      // no column to order by. Filter with used_by_bookings=no instead.
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {(['import', 'bookings', 'api'] as const).map((key) => {
            const facet = p.row.used_by?.[key];
            if (!facet) return null;
            return (
              <CellActionsPopover
                key={key}
                auto
                title={facet.detail ?? facet.reason}
                actions={[
                  filterAction(
                    `Filter by ${key} = ${facet.state}`,
                    `used_by_${key}`,
                    facet.state,
                    onQuickFilter,
                  ),
                  // The finding is the interesting query, so offer it straight
                  // from any row rather than only from one that already is one.
                  filterAction(
                    `Show ${key} problems only`,
                    `used_by_${key}`,
                    'no',
                    onQuickFilter,
                  ),
                ]}
                sx={CHIP_TRIGGER_SX}
              >
                <FacetCell label={key} facet={facet} />
              </CellActionsPopover>
            );
          })}
        </Box>
      ),
    },
    {
      field: 'integration',
      headerName: 'Integration',
      width: 150,
      sortable: true,
      filterable: false,
      renderCell: (p) => {
        if (!p.row.integration) {
          return (
            <CellActionsPopover
              auto
              title="Unscoped: applies to every restech. This is the norm."
              actions={[
                filterAction('Filter by unscoped rows', 'integration', 'none', onQuickFilter),
              ]}
              sx={{ color: 'text.disabled' }}
            >
              any
            </CellActionsPopover>
          );
        }
        const label = p.row.restech_account
          ? `${p.row.integration} / ${p.row.restech_account}`
          : p.row.integration;
        return (
          <CellActionsPopover
            auto
            actions={[
              filterAction(
                'Filter by this integration',
                'integration',
                p.row.integration,
                onQuickFilter,
              ),
              ...(p.row.restech_account
                ? [
                    filterAction(
                      'Filter by this account',
                      'account',
                      p.row.restech_account,
                      onQuickFilter,
                    ),
                  ]
                : []),
              copyAction('Copy integration', label),
            ]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {label}
          </CellActionsPopover>
        );
      },
    },
    {
      field: 'queryable',
      headerName: 'API queryable',
      width: 130,
      sortable: true,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={
            p.row.api_queryable
              ? 'May be sent to the vendor API as a query argument.'
              : 'Matching-only key: never sent to the vendor as a query argument.'
          }
          actions={[
            filterAction(
              p.row.api_queryable ? 'Filter by queryable' : 'Filter by non-queryable',
              'queryable',
              p.row.api_queryable ? 'yes' : 'no',
              onQuickFilter,
            ),
          ]}
          sx={{ color: p.row.api_queryable ? 'success.main' : 'text.secondary' }}
        >
          {p.row.api_queryable ? 'yes' : 'no'}
        </CellActionsPopover>
      ),
    },
    {
      field: 'comment',
      headerName: 'Notes',
      flex: 1,
      minWidth: 180,
      sortable: true,
      filterable: false,
      renderCell: (p) => {
        const note = p.row.comment?.trim();
        const link = p.row.ext_link?.trim();
        if (!note && !link) return dash;
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
            {link ? (
              <Tooltip title={link}>
                <Box
                  component="a"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: 'inline-flex', color: 'primary.main' }}
                  aria-label="Open the linked product page"
                >
                  <OpenInNewIcon fontSize="small" />
                </Box>
              </Tooltip>
            ) : null}
            {note ? (
              <CellActionsPopover
                auto
                title={note}
                actions={[
                  copyAction('Copy note', note),
                  filterAction('Filter by this note', 'comment', note, onQuickFilter),
                ]}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {note}
              </CellActionsPopover>
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 170,
      sortable: true,
      filterable: false,
      renderCell: (p) => (p.row.created_ts ? <TsValue value={p.row.created_ts} /> : dash),
    },
  );

  if (onRowMenu) {
    cols.push({
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: 'right',
      renderCell: (p) => (
        <IconButton
          size="small"
          aria-label="Row actions"
          onClick={(e) => {
            // The row click opens the drawer; without this the menu and the
            // drawer both open on one press.
            e.stopPropagation();
            onRowMenu(e.currentTarget, p.row);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    });
  }

  return cols;
}
